<script setup lang="ts">
import { computed, PropType } from 'vue';
import AppToolbarItem from '../components/app-toolbar-item.vue';
import { IAppToolbarItem, TableBasedContainerToolbarOptions } from '@/stores/core/models';

const emit = defineEmits(['new', 'refresh', 'delete']);

const props = defineProps({
  title: {
    type: String,
  },
  badge: {
    type: Number,
    default: undefined,
  },
  options: {
    type: Object as PropType<TableBasedContainerToolbarOptions>,
    required: true,
  },
  hideAll: {
    type: Boolean,
  },
});

const isDeleteDisabled = computed(() => !props.options.canDelete || !props.options.isDeleteEnabled);

const toolbarItems = computed(
  () =>
    [
      {
        id: 'btnRefresh',
        icon: 'hugeicons:arrow-reload-horizontal',
        severity: 'secondary',
        click: () => emit('refresh'),
        isHidden: !props.options.showRefresh,
        label: props.options.showRefreshLabel ? 'core.refresh' : undefined,
      },
      {
        id: 'btnDelete',
        icon: 'hugeicons:delete-03',
        severity: 'danger',
        click: () => emit('delete'),
        isHidden: !props.options.showDelete,
        label: 'core.delete',
        disabled: isDeleteDisabled.value,
      },
      {
        id: 'btnNew',
        icon: 'hugeicons:plus-sign-circle',
        severity: 'primary',
        click: () => emit('new'),
        isHidden: !props.options.showAddNew,
        label: props.options.showAddNewLabel ? props.options.newLabel : undefined,
        isPrimary: true,
        disabled: props.options.disableAddNew
      },
      ...props.options.items,
    ] as IAppToolbarItem[]
);
</script>

<template>
  <div class="flex items-center justify-between" v-if="!hideAll" :class="{ 'border-b border-surface-200 dark:!border-gray-800 mb-8': hideAll }">
    <div
      v-if="title"
      class="flex items-center gap-3"
      :class="{ 'pb-4': hideAll }"
    >
      <span class="dark:border-r-surface-700 font-semibold text-[30px] text-gray-900 dark:text-gray-100">{{ title }}</span>
      <span
        v-if="badge !== undefined && badge > 0"
        class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-sm font-medium text-gray-600 dark:text-gray-300"
      >
        {{ badge }}
      </span>
    </div>
    <div class="flex items-center gap-3" v-if="!hideAll">
      <!----------------------------
      --   Regular Toolbar Items  --
      ------------------------------>
      <div v-if="!hideAll" class="shrink-0" v-for="item in toolbarItems.filter((x) => !x.isHidden)">
        <app-toolbar-item @click="item.click" :item="item" />
      </div>
    </div>
  </div>
</template>
